/**
 * AppProviders.
 *
 *   <Provider store>
 *     <BrowserRouter>
 *       <BrandingProvider>           - tenant CSS vars on <html>
 *         <AntdThemeProvider>        - Ant Design seeds as hex (hover/danger)
 *           <ToastProvider>{children}</ToastProvider>
 *         </AntdThemeProvider>
 *       </BrandingProvider>
 *     </BrowserRouter>
 *   </Provider>
 *
 * Seed colors Antd derives (primary hover, danger) are hex from branding.
 * Surfaces stay on CSS variables so cards/borders still follow the tokens.
 */
import type { ReactNode } from "react";

import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import { store } from "@/app/store";

import { BrandingProvider } from "@/features/manifest/ui/BrandingProvider";

import { getRouterBasename } from "@/shared/lib/tenantSlug";
import { ToastProvider } from "@/shared/ui/Toast";

import { AntdThemeProvider } from "./AntdThemeProvider";


interface Props {
  children: ReactNode;
}

const ROUTER_BASENAME = getRouterBasename();

/** Top-level provider stack (Redux store, router, Antd config, etc.). */
export function AppProviders({ children }: Props) {
  return (
    <ReduxProvider store={store}>
      <BrowserRouter basename={ROUTER_BASENAME}>
        <BrandingProvider>
          <AntdThemeProvider>
            <ToastProvider>{children}</ToastProvider>
          </AntdThemeProvider>
        </BrandingProvider>
      </BrowserRouter>
    </ReduxProvider>
  );
}
